/**
 * planTimeLayout.ts
 *
 * Dieses Modul berechnet das visuelle Layout von Zeitslot-Ereignissen
 * (TimeSlot-Events) in einem spaltenbasierten Zeitplan (z. B. Wochenplan).
 *
 * Die wichtigsten Konzepte:
 *  - `TimeSlotSourceEvent`  : Ein Rohereignis mit Startzeit, Endzeit und Spaltenzuweisung.
 *  - `TimeSlotSegment`      : Ein verarbeitetes Segment, das genau in eine Spalte passt
 *                             (Ereignisse können über mehrere Spalten/Tage gehen).
 *  - `TimeSlotOverflowGroup`: Enthält Segmente, die wegen `maxVisibleLanes` ausgeblendet wurden.
 *  - `TimeSlotLayout`       : Das fertige Layout-Ergebnis für die Darstellung.
 *
 * Ablauf:
 *  1. `buildTimeSlotLayout` → Haupteintrittspunkt
 *  2. `splitEventAcrossColumns` → Zerlegt Events, die über Tage gehen
 *  3. `layoutColumnSegments` → Weist jeder Spalte Lanes zu und erkennt Überlauf
 */

/**
 * Repräsentiert ein Rohereignis im Zeitplan, bevor das Layout berechnet wird.
 * Zeiten werden in Minuten seit Mitternacht (0–1439) angegeben.
 */
export type TimeSlotSourceEvent<Row extends Record<string, string> = Record<string, string>> = {
  /** Eindeutige ID des Ereignisses */
  id: string;
  /** Zusätzliche Zeilendaten (z. B. Lehrer, Raum, Klasse) als Key-Value-Map */
  row: Row;
  /** Index der Spalte (z. B. Wochentag), in der das Ereignis beginnt */
  columnIndex: number;
  /** Anzeigename der Spalte */
  columnName: string;
  /** Name der Phase (z. B. Unterrichtsphase) */
  phaseName: string;
  /** Startzeit in Minuten seit Mitternacht */
  startMin: number;
  /** Endzeit in Minuten seit Mitternacht */
  endMin: number;
};

/**
 * Ein verarbeitetes Segment eines Ereignisses, das genau einer Spalte zugeordnet ist.
 * Wenn ein Ereignis über Mitternacht hinausgeht, wird es in mehrere Segmente aufgeteilt.
 * Enthält zusätzlich Layout-Informationen (Lane, Sichtbarkeit, Gruppenreferenz).
 */
export type TimeSlotSegment<Row extends Record<string, string> = Record<string, string>> = {
  /** Eindeutige Segment-ID (Format: `{eventId}::seg{index}`) */
  id: string;
  /** ID des Ursprungsereignisses */
  eventId: string;
  /** Zusätzliche Zeilendaten des Ursprungsereignisses */
  row: Row;
  /** Index der Spalte, in der dieses Segment liegt */
  columnIndex: number;
  /** Anzeigename der Spalte */
  columnName: string;
  /** Name der Phase */
  phaseName: string;
  /** Startzeit dieses Segments in Minuten seit Mitternacht */
  startMin: number;
  /** Endzeit dieses Segments in Minuten seit Mitternacht */
  endMin: number;
  /** Lane-Index innerhalb der Spaltengruppe (0 = ganz links) */
  laneIndex: number;
  /** Gesamtanzahl der Lanes in der Gruppe (für Breitenberechnung) */
  laneCount: number;
  /** ID der Überlappungsgruppe, zu der dieses Segment gehört */
  groupId: string;
  /** true = Segment ist ausgeblendet (weil maxVisibleLanes überschritten) */
  hidden: boolean;
  /** true = Das Ereignis kommt von der vorherigen Spalte (hat ein Vorgänger-Segment) */
  continuesFromPrev: boolean;
  /** true = Das Ereignis geht in die nächste Spalte weiter (hat ein Nachfolger-Segment) */
  continuesToNext: boolean;
};

/**
 * Repräsentiert eine Gruppe von Segmenten, bei der mindestens ein Segment
 * ausgeblendet wurde, weil `maxVisibleLanes` überschritten wurde.
 * Wird verwendet, um einen "+N weitere"-Hinweis in der UI anzuzeigen.
 */
export type TimeSlotOverflowGroup<Row extends Record<string, string> = Record<string, string>> = {
  /** ID der Gruppe (identisch mit der `groupId` der zugehörigen Segmente) */
  id: string;
  /** Index der Spalte, zu der diese Gruppe gehört */
  columnIndex: number;
  /** Früheste Startzeit aller Segmente in der Gruppe (in Minuten) */
  startMin: number;
  /** Späteste Endzeit aller Segmente in der Gruppe (in Minuten) */
  endMin: number;
  /** Anzahl der ausgeblendeten Segmente */
  hiddenCount: number;
  /** Gesamtanzahl der Segmente in der Gruppe (sichtbar + ausgeblendet) */
  totalCount: number;
  /** Liste aller Ereignis-IDs in der Gruppe (ohne Duplikate) */
  eventIds: string[];
  /** Alle Segmente der Gruppe (inkl. ausgeblendeter) */
  segments: TimeSlotSegment<Row>[];
};

/**
 * Das fertige Layout-Ergebnis, das direkt für die Darstellung genutzt wird.
 */
export type TimeSlotLayout<Row extends Record<string, string> = Record<string, string>> = {
  /** Alle berechneten Segmente (inkl. Lane- und Sichtbarkeits-Informationen) */
  segments: TimeSlotSegment<Row>[];
  /** Gruppen mit ausgeblendeten Segmenten (für "+N weitere"-Anzeige) */
  overflowGroups: TimeSlotOverflowGroup<Row>[];
  /** Gerundete Startzeit des sichtbaren Zeitbereichs (in Minuten) */
  visibleStartMin: number;
  /** Gerundete Endzeit des sichtbaren Zeitbereichs (in Minuten) */
  visibleEndMin: number;
};

/** Optionen für `buildTimeSlotLayout` */
type BuildLayoutOptions = {
  /** Anzahl der Spalten im Zeitplan (z. B. 5 für Mo–Fr) */
  columnCount: number;
  /** Fallback-Startzeit (Minuten) falls keine Ereignisse vorhanden sind */
  fallbackStartMin?: number;
  /** Fallback-Endzeit (Minuten) falls keine Ereignisse vorhanden sind */
  fallbackEndMin?: number;
  /** Maximale Anzahl sichtbarer Lanes pro Überlappungsgruppe (Standard: 3) */
  maxVisibleLanes?: number;
  /** Schrittweite für die Zeitachsen-Rasterung in Minuten (Standard: 60) */
  slotStepMin?: number;
};

/** Standard-Sichtbarkeitsbereich: 08:00 Uhr */
const DEFAULT_VISIBLE_START_MIN = 8 * 60;
/** Standard-Sichtbarkeitsbereich: 18:00 Uhr */
const DEFAULT_VISIBLE_END_MIN = 18 * 60;

/**
 * Parst einen Zeitstring im Format "HH:MM" und gibt die Anzahl der Minuten
 * seit Mitternacht zurück. Gibt `null` zurück, wenn der String ungültig ist.
 *
 * @param value - Zeitstring, z. B. "08:30" oder "14:00"
 * @returns Minuten seit Mitternacht (0–1439) oder `null`
 */
export function parseHHMM(value?: string): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Formatiert eine Minutenanzahl (seit Mitternacht) als "HH:MM"-String.
 * Werte außerhalb von 0–1439 werden normalisiert (Modulo 1440).
 *
 * @param min - Minuten seit Mitternacht
 * @returns Formatierter Zeitstring, z. B. "08:30"
 */
export function formatMinutes(min: number): string {
  if (!isFinite(min)) return "";
  const normalized = ((min % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const hh = hours < 10 ? `0${hours}` : String(hours);
  const mm = minutes < 10 ? `0${minutes}` : String(minutes);
  return `${hh}:${mm}`;
}

/**
 * Hauptfunktion: Berechnet das vollständige Layout aller Ereignisse für den Zeitplan.
 *
 * Schritte:
 *  1. Jedes Ereignis wird in Spalten-Segmente aufgeteilt (`splitEventAcrossColumns`).
 *  2. Für jede Spalte werden Lanes zugewiesen und Überlaufgruppen erkannt (`layoutColumnSegments`).
 *  3. Der sichtbare Zeitbereich wird aus den tatsächlichen Ereignisdaten berechnet
 *     und auf `slotStepMin` gerundet.
 *
 * @param events  - Liste der Rohereignisse
 * @param options - Konfigurationsoptionen (Spaltenanzahl, maximale Lanes, etc.)
 * @returns Fertiges Layout mit Segmenten, Überlaufgruppen und sichtbarem Zeitbereich
 */
export function buildTimeSlotLayout<Row extends Record<string, string>>(
  events: TimeSlotSourceEvent<Row>[],
  options: BuildLayoutOptions
): TimeSlotLayout<Row> {
  const maxVisibleLanes = Math.max(1, options.maxVisibleLanes ?? 3);
  const slotStepMin = Math.max(15, options.slotStepMin ?? 60);

  // Alle Events in spaltenspezifische Segmente aufteilen
  const rawSegments = events.reduce<TimeSlotSegment<Row>[]>((acc, event) => {
    acc.push(...splitEventAcrossColumns(event, options.columnCount));
    return acc;
  }, []);

  const laidOutSegments: TimeSlotSegment<Row>[] = [];
  const overflowGroups: TimeSlotOverflowGroup<Row>[] = [];

  // Für jede Spalte das Lane-Layout berechnen
  for (let columnIndex = 0; columnIndex < options.columnCount; columnIndex += 1) {
    const columnSegments = rawSegments.filter((segment) => segment.columnIndex === columnIndex);
    const { segments, groups } = layoutColumnSegments(columnSegments, columnIndex, maxVisibleLanes);
    laidOutSegments.push(...segments);
    overflowGroups.push(...groups);
  }

  // Sichtbaren Zeitbereich aus den Ereignisdaten ermitteln (oder Fallback nutzen)
  const visibleStartSource =
    rawSegments.length > 0
      ? Math.min(...rawSegments.map((segment) => segment.startMin))
      : options.fallbackStartMin ?? DEFAULT_VISIBLE_START_MIN;
  const visibleEndSource =
    rawSegments.length > 0
      ? Math.max(...rawSegments.map((segment) => segment.endMin))
      : options.fallbackEndMin ?? DEFAULT_VISIBLE_END_MIN;

  const visibleStartMin = roundDown(Math.max(0, visibleStartSource), slotStepMin);
  const visibleEndMin = Math.max(
    visibleStartMin + slotStepMin,
    roundUp(Math.min(1440, visibleEndSource), slotStepMin)
  );

  return {
    segments: laidOutSegments,
    overflowGroups,
    visibleStartMin,
    visibleEndMin,
  };
}

/**
 * Teilt ein einzelnes Ereignis in ein oder mehrere Spalten-Segmente auf.
 *
 * Wenn ein Ereignis über Mitternacht hinausgeht (endMin > 1440 relativ zum Start),
 * wird es auf die nächste Spalte übertragen (Index + 1), beginnend bei Minute 0.
 * Das letzte Segment wird mit `continuesToNext = true` markiert, wenn das Ereignis
 * die maximale Spaltenanzahl überschreitet.
 *
 * @param event       - Das Quellereignis
 * @param columnCount - Gesamtanzahl der Spalten
 * @returns Array von Segmenten (eines pro betroffener Spalte)
 */
function splitEventAcrossColumns<Row extends Record<string, string>>(
  event: TimeSlotSourceEvent<Row>,
  columnCount: number
): TimeSlotSegment<Row>[] {
  const safeDuration = Math.max(0, event.endMin - event.startMin);
  if (safeDuration <= 0) return [];

  const segments: TimeSlotSegment<Row>[] = [];
  let remainingDuration = safeDuration;
  let columnIndex = event.columnIndex;
  let segmentStartMin = event.startMin;
  let segmentIndex = 0;

  while (remainingDuration > 0 && columnIndex < columnCount) {
    const availableInDay = Math.max(0, 1440 - segmentStartMin);
    if (availableInDay === 0) break;

    const segmentDuration = Math.min(remainingDuration, availableInDay);
    remainingDuration -= segmentDuration;
    const continuesToNext = remainingDuration > 0;

    segments.push({
      id: `${event.id}::seg${segmentIndex}`,
      eventId: event.id,
      row: event.row,
      columnIndex,
      columnName: event.columnName,
      phaseName: event.phaseName,
      startMin: segmentStartMin,
      endMin: segmentStartMin + segmentDuration,
      laneIndex: 0,
      laneCount: 1,
      groupId: "",
      hidden: false,
      continuesFromPrev: segmentIndex > 0,
      continuesToNext,
    });

    columnIndex += 1;
    segmentStartMin = 0;
    segmentIndex += 1;
  }

  if (remainingDuration > 0 && segments.length > 0) {
    // Das letzte Segment als "weiterführend" markieren, wenn Spalten erschöpft sind
    segments[segments.length - 1] = {
      ...segments[segments.length - 1],
      continuesToNext: true,
    };
  }

  return segments;
}

/**
 * Berechnet das Lane-Layout für alle Segmente einer einzelnen Spalte.
 *
 * Algorithmus:
 *  - Segmente werden nach Startzeit sortiert (bei Gleichstand: längere zuerst).
 *  - Überlappende Segmente werden zu einer Gruppe zusammengefasst.
 *  - Innerhalb einer Gruppe wird jedem Segment die nächste freie Lane zugewiesen.
 *  - Wenn die Anzahl der Lanes `maxVisibleLanes` übersteigt, werden die überschüssigen
 *    Segmente als `hidden = true` markiert und eine `TimeSlotOverflowGroup` erzeugt.
 *
 * @param segments        - Alle Segmente der Spalte
 * @param columnIndex     - Index der Spalte (für die Gruppen-ID)
 * @param maxVisibleLanes - Maximale Anzahl sichtbarer Lanes
 * @returns Fertig layoutete Segmente und eventuelle Überlaufgruppen
 */
function layoutColumnSegments<Row extends Record<string, string>>(
  segments: TimeSlotSegment<Row>[],
  columnIndex: number,
  maxVisibleLanes: number
): {
  segments: TimeSlotSegment<Row>[];
  groups: TimeSlotOverflowGroup<Row>[];
} {
  if (segments.length === 0) {
    return { segments: [], groups: [] };
  }

  const sorted = [...segments].sort((a, b) => {
    const byStart = a.startMin - b.startMin;
    if (byStart !== 0) return byStart;
    // Bei gleichem Start: längere Ereignisse zuerst (für stabileres Layout)
    const byLongerFirst = (b.endMin - b.startMin) - (a.endMin - a.startMin);
    if (byLongerFirst !== 0) return byLongerFirst;
    return a.id.localeCompare(b.id);
  });

  const finalizedSegments: TimeSlotSegment<Row>[] = [];
  const overflowGroups: TimeSlotOverflowGroup<Row>[] = [];

  let groupSeq = 0;
  let currentGroup: TimeSlotSegment<Row>[] = [];
  let currentGroupMaxEnd = -1;
  let laneEnds: number[] = [];

  const flushGroup = () => {
    if (currentGroup.length === 0) return;

    const laneCount = currentGroup.reduce(
      (max, segment) => Math.max(max, segment.laneIndex + 1),
      1
    );
    const groupId = `col${columnIndex}-group${groupSeq}`;
    groupSeq += 1;

    const withMeta = currentGroup.map((segment) => ({
      ...segment,
      groupId,
      laneCount,
      hidden: laneCount > maxVisibleLanes && segment.laneIndex >= maxVisibleLanes,
    }));
    finalizedSegments.push(...withMeta);

    const hiddenSegments = withMeta.filter((segment) => segment.hidden);
    if (hiddenSegments.length > 0) {
      overflowGroups.push({
        id: groupId,
        columnIndex,
        startMin: Math.min(...withMeta.map((segment) => segment.startMin)),
        endMin: Math.max(...withMeta.map((segment) => segment.endMin)),
        hiddenCount: hiddenSegments.length,
        totalCount: withMeta.length,
        eventIds: Array.from(new Set(withMeta.map((segment) => segment.eventId))),
        segments: withMeta,
      });
    }

    currentGroup = [];
    currentGroupMaxEnd = -1;
    laneEnds = [];
  };

  sorted.forEach((segment) => {
    // Wenn das neue Segment nach der aktuellen Gruppe beginnt → Gruppe abschließen
    if (currentGroup.length > 0 && segment.startMin >= currentGroupMaxEnd) {
      flushGroup();
    }

    // Nächste freie Lane finden (eine Lane ist frei, wenn ihr letztes Segment bereits geendet hat)
    let laneIndex = laneEnds.findIndex((endMin) => endMin <= segment.startMin);
    if (laneIndex === -1) {
      // Keine freie Lane → neue Lane hinzufügen
      laneIndex = laneEnds.length;
    }
    laneEnds[laneIndex] = segment.endMin;

    currentGroup.push({
      ...segment,
      laneIndex,
    });
    currentGroupMaxEnd = Math.max(currentGroupMaxEnd, segment.endMin);
  });

  flushGroup();

  return {
    segments: finalizedSegments,
    groups: overflowGroups,
  };
}

/**
 * Rundet einen Wert auf das nächste Vielfache von `step` ab.
 * Wird für `visibleStartMin` verwendet.
 */
function roundDown(value: number, step: number): number {
  return Math.floor(value / step) * step;
}

/**
 * Rundet einen Wert auf das nächste Vielfache von `step` auf.
 * Wird für `visibleEndMin` verwendet.
 */
function roundUp(value: number, step: number): number {
  return Math.ceil(value / step) * step;
}
